import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueries } from '@tanstack/react-query';

import {
  Alert,
  EmptyState,
  Flex,
  Icon,
  Skeleton,
  Spinner,
  Switch,
  Text,
  toast,
} from '@kognitos/lattice';
import {
  type Bdkv2BookDescriptor,
  type V2BookConnectionDescriptor,
  V2BookConnectionState,
} from '@/generated/hooks/bdk/models';
import {
  getRetrieveWorkspaceBookQueryKey,
  retrieveWorkspaceBook,
  useRetrieveAllWorkspaceBookConnections,
} from '@/generated/hooks/bdk/v2/services/library';
import { ConnectionStatusTag } from '@/modules/integrations/components/ConnectionStatusTag';
import { isExcludedBookName } from '@/modules/integrations/constants';
import { captureException } from '@/modules/sentry';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { FilterBuilder } from '@/shared/utils/filter-builder';
import { convertToDataUrl } from '@/shared/utils/image-utils';

import { useAgentBuilderApi } from '../hooks/use-agent-builder-api';
import { useAgentConnectedBooks } from '../hooks/use-agent-connected-books';

/** Page size for the workspace connections list (matches the integrations page). */
const CONNECTIONS_PAGE_SIZE = 100;

/**
 * Server-side AIP-160 filter: only healthy connections are worth offering, since
 * attaching a pending or failed one hands the agent a tool it can't call.
 *
 * The field is `state` (not `status.state`) and the value is the *bare* enum name
 * — the backend's matcher stringifies the state as `READY`, not
 * `BOOK_CONNECTION_STATE_READY`. An unrecognized field or value matches nothing
 * and still returns 200, so a typo here reads as "workspace has no connections"
 * rather than as an error.
 */
const READY_CONNECTIONS_FILTER = FilterBuilder.create()
  .equals('state', 'READY')
  .build();

interface IAgentConnectionsListProps {
  /** Created agent id, or null before the agent is created. */
  agentId: string | null;
  /** Config thread id, or null while it's still being resolved. */
  threadId: string | null;
  /**
   * Hides the "Active connections in this workspace" eyebrow. Set by the
   * builder's config pane, where the accordion row above already reads
   * "Integrations" and the eyebrow was a heading over a heading. The creation
   * wizard keeps it — there the list sits among other unlabelled content.
   */
  hideLabel?: boolean;
  /**
   * Renders the list as a read-only report: the toggles show state but can't be
   * flipped. Set by the builder's config pane while its Edit mode is off, so
   * the whole pane is consistently either a report or an editor.
   */
  readOnly?: boolean;
}

/**
 * The agent's integrations, expressed as the workspace's existing connections.
 *
 * This replaces the book-catalog grid: browsing every integration in the
 * catalog asked the user to pick a product and *then* discover whether a
 * connection for it existed. What actually determines whether an agent can use
 * an integration is the connection, so we list the connections the workspace
 * already has and let the user add or remove each one directly.
 *
 * Rows mirror the Integrations → Connections page: the book's real icon, the
 * connection name and the integration's display name. Only READY connections are
 * offered — the query filters on state server-side, so an unhealthy connection is
 * the Integrations page's problem, not something to offer an agent here. One the
 * agent already uses is still listed, with its status, so it can be switched
 * off; it can't be switched back on until it's READY again. Adding
 * creates a `bdk` tool source for the connection; removing deletes that tool
 * source. Connected state is derived from the agent's tool sources, so it stays
 * correct regardless of how the connection was attached (here or by the Builder
 * from chat).
 */
export function AgentConnectionsList({
  agentId,
  threadId,
  hideLabel = false,
  readOnly = false,
}: IAgentConnectionsListProps) {
  const { t } = useTranslation('agent-builder');
  const organization = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspace = useUserPreferencesStore(selectCurrentWorkspaceId);
  const { attachBookConnection, removeToolSource } = useAgentBuilderApi();

  // connection_ids currently being attached / detached, for per-row spinners.
  const [busyConnectionIds, setBusyConnectionIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const setBusy = (connectionId: string, busy: boolean) =>
    setBusyConnectionIds(previous => {
      const next = new Set(previous);
      if (busy) next.add(connectionId);
      else next.delete(connectionId);
      return next;
    });

  const {
    data,
    isLoading: isLoadingConnections,
    error: connectionsError,
  } = useRetrieveAllWorkspaceBookConnections(
    organization,
    workspace,
    { page_size: CONNECTIONS_PAGE_SIZE, filter: READY_CONNECTIONS_FILTER },
    { query: { enabled: Boolean(organization && workspace) } },
  );

  // What the agent already uses, keyed by connection_id (not by book) so a
  // workspace with two connections to the same product tracks them separately.
  // Read straight from the tool sources so a just-attached connection shows as
  // on before its descriptor is fetched, rather than as a second chance to add.
  const {
    sourceByConnectionId,
    attachedConnections,
    isLoading: isLoadingConnected,
  } = useAgentConnectedBooks(agentId, threadId);

  // What was attached when the list first loaded. Rows sort on this rather
  // than the live state, so a toggled row doesn't jump to the top.
  const [initiallyAttached, setInitiallyAttached] =
    useState<ReadonlySet<string> | null>(null);
  if (!initiallyAttached && !isLoadingConnected && data) {
    setInitiallyAttached(new Set(sourceByConnectionId.keys()));
  }

  const connections = useMemo(() => {
    const ready = data?.data.book_connections ?? [];
    const readyIds = new Set(ready.map(connection => connection.connection_id));
    // Attached connections the READY filter left out, so they can be removed.
    const notReadyAttached = attachedConnections.filter(
      connection => !readyIds.has(connection.connection_id),
    );
    // Internal-only books (e.g. the email book) aren't user-facing integrations.
    const external = [...ready, ...notReadyAttached].filter(
      connection => !isExcludedBookName(connection.book_name),
    );
    // Attached first, then alphabetical — the agent's own integrations are what
    // you're checking when you open this section.
    const attached = initiallyAttached ?? sourceByConnectionId;
    return [...external].sort((a, b) => {
      const aOn = Number(attached.has(a.connection_id ?? ''));
      const bOn = Number(attached.has(b.connection_id ?? ''));
      if (aOn !== bOn) return bOn - aOn;
      return (a.connection_name ?? '').localeCompare(
        b.connection_name ?? '',
        undefined,
        { sensitivity: 'base' },
      );
    });
  }, [data, attachedConnections, initiallyAttached, sourceByConnectionId]);

  // The books these connections belong to, fetched per (name, version) so we
  // only pull the handful actually referenced — the Integrations page paginates
  // the whole catalog for this, which is far more than a panel needs.
  const bookRefs = useMemo(() => {
    const seen = new Set<string>();
    const refs: { name: string; version: string }[] = [];
    for (const connection of connections) {
      if (!connection.book_name || !connection.book_version) continue;
      const key = `${connection.book_name}@${connection.book_version}`;
      if (seen.has(key)) continue;
      seen.add(key);
      refs.push({
        name: connection.book_name,
        version: connection.book_version,
      });
    }
    return refs;
  }, [connections]);

  const bookQueries = useQueries({
    queries: bookRefs.map(ref => ({
      queryKey: getRetrieveWorkspaceBookQueryKey(
        organization,
        workspace,
        ref.name,
        ref.version,
      ),
      queryFn: () =>
        retrieveWorkspaceBook(organization, workspace, ref.name, ref.version),
      enabled: Boolean(organization && workspace),
      staleTime: Infinity,
    })),
  });

  // "name@version" → descriptor, for the row's icon and display name.
  const booksByRef = useMemo(() => {
    const map = new Map<string, Bdkv2BookDescriptor>();
    for (const query of bookQueries) {
      const book = query.data?.data.book;
      if (book?.name && book.version) {
        map.set(`${book.name}@${book.version}`, book);
      }
    }
    return map;
  }, [bookQueries]);

  // Whether any book descriptor is still in flight. The rows can't show a
  // trustworthy integration name until these land: falling back to the raw
  // `book_name` slug meant the label visibly rewrote itself ("idp" →
  // "Intelligent Document Processing") a second after paint. A skeleton is
  // honest about not knowing yet, and doesn't move the text under the cursor.
  const isLoadingBooks = bookQueries.some(query => query.isLoading);

  const hasAnyConnections = connections.length > 0 && !isLoadingConnections;

  const handleAdd = async (connection: V2BookConnectionDescriptor) => {
    if (!agentId || !threadId || !connection.connection_id) return;
    const connectionId = connection.connection_id;
    setBusy(connectionId, true);
    try {
      await attachBookConnection(agentId, threadId, connectionId);
      toast.success(t('agentBuilder.wizard.connect.connectionEstablished'));
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
        { tags: { module: 'agent-builder', action: 'attachBookConnection' } },
      );
      toast.error(t('agentBuilder.wizard.connect.connectionEstablishError'));
    } finally {
      setBusy(connectionId, false);
    }
  };

  const handleRemove = async (connection: V2BookConnectionDescriptor) => {
    const connectionId = connection.connection_id ?? '';
    const sourceId = sourceByConnectionId.get(connectionId);
    if (!agentId || !threadId || !sourceId) return;
    setBusy(connectionId, true);
    try {
      await removeToolSource(agentId, threadId, sourceId);
      toast.success(t('agentBuilder.wizard.connect.connectionRemoved'));
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
        { tags: { module: 'agent-builder', action: 'removeToolSource' } },
      );
      toast.error(t('agentBuilder.wizard.connect.connectionRemoveError'));
    } finally {
      setBusy(connectionId, false);
    }
  };

  if (connectionsError) {
    return (
      <Alert variant="destructive">
        {t('agentBuilder.wizard.connect.connectionsError')}
      </Alert>
    );
  }

  return (
    <Flex direction="column" gap={3} className="w-full min-w-0">
      {/* A subsection label, not a heading: smaller, muted and uppercase so it
          sits clearly *under* the "Tools & Integrations" accordion header
          rather than competing with it at the same size and weight. Matches the
          Actions label below, so the two subsections still read as peers. */}
      {!hideLabel ? (
        <Text
          level="xSmall"
          weight="semibold"
          color="muted"
          className="min-w-0 uppercase tracking-wide"
        >
          {t('agentBuilder.wizard.connect.connectionsTitle')}
        </Text>
      ) : null}

      {isLoadingConnections || isLoadingConnected ? (
        <Flex align="center" justify="center" className="min-h-24 w-full">
          <Spinner size="lg" />
        </Flex>
      ) : !hasAnyConnections ? (
        // Nothing to pick from: the workspace has no connections at all. The fix
        // lives on the Integrations page, so point there.
        <EmptyState
          icon="Blocks"
          title={t('agentBuilder.wizard.connect.noWorkspaceConnectionsTitle')}
          description={t('agentBuilder.wizard.connect.noWorkspaceConnections')}
          className="w-full"
        />
      ) : (
        <Flex direction="column" className="w-full min-w-0">
          {connections.map(connection => {
            const connectionId = connection.connection_id ?? '';
            const isAttached = sourceByConnectionId.has(connectionId);
            const isBusy = busyConnectionIds.has(connectionId);
            const connectionName = connection.connection_name || connectionId;
            const state = connection.status?.state;
            const isReady =
              !state ||
              state === V2BookConnectionState.BOOK_CONNECTION_STATE_READY;
            const book = booksByRef.get(
              `${connection.book_name}@${connection.book_version}`,
            );
            // Only the book's own display name is shown. The raw `book_name`
            // slug is deliberately NOT used as a fallback — it's a different
            // string, so using it while the descriptor loads made the label
            // rewrite itself mid-read.
            const integrationName = book?.display_name ?? '';
            const iconUrl = convertToDataUrl(book?.icon);

            return (
              <Flex
                key={connectionId}
                align="center"
                gap={3}
                className="w-full min-w-0 border-b border-border py-3 last:border-b-0"
                data-testid={`agent-connection-${connectionId}`}
              >
                {isLoadingBooks ? (
                  <Skeleton className="size-8 shrink-0 rounded" />
                ) : iconUrl ? (
                  // Decorative: the integration name is already the adjacent
                  // text, so an alt would just repeat it to a screen reader.
                  <img
                    src={iconUrl}
                    // eslint-disable-next-line no-restricted-syntax -- reason: intentionally empty alt on a decorative icon, not a user-facing string to translate
                    alt=""
                    className="size-8 shrink-0 rounded"
                  />
                ) : (
                  // Descriptor resolved but carries no icon.
                  <Flex
                    align="center"
                    justify="center"
                    className="size-8 shrink-0 rounded bg-muted text-muted-foreground"
                  >
                    <Icon type="Blocks" size="sm" />
                  </Flex>
                )}

                <Flex direction="column" gap={1} className="min-w-0 flex-1">
                  {/* The connection name comes from the connections list, so
                      it's correct on first paint and never re-writes. */}
                  <Text weight="medium" className="w-full break-words">
                    {connectionName}
                  </Text>
                  {isLoadingBooks ? (
                    <Skeleton className="h-3 w-32" />
                  ) : integrationName ? (
                    <Text level="small" color="muted" className="truncate">
                      {integrationName}
                    </Text>
                  ) : null}
                </Flex>

                {/* A switch, not Add/Remove buttons: whether the agent may use
                    a connection is an on/off state, and the button form stated
                    the *action* rather than the state — so a row read "Add"
                    while off and "Remove" while on, leaving the current value
                    to be inferred from the verb. This also matches how
                    Automations toggles its rows in the section below. */}
                {!isReady ? (
                  <ConnectionStatusTag
                    connections={[connection]}
                    status={state}
                  />
                ) : null}

                {isBusy ? (
                  <Spinner size="sm" className="shrink-0" />
                ) : (
                  <Switch
                    checked={isAttached}
                    disabled={
                      readOnly ||
                      !agentId ||
                      !threadId ||
                      (!isAttached && !isReady)
                    }
                    onCheckedChange={next =>
                      next ? handleAdd(connection) : handleRemove(connection)
                    }
                    aria-label={connectionName}
                    className="shrink-0"
                    data-testid={`agent-connection-toggle-${connectionId}`}
                  />
                )}
              </Flex>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
}
